import { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";

const fmtIt = (d) => (d || "").split("-").reverse().join("/");

export default function FiscalChecklist({ item, onClose }) {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [newLink, setNewLink] = useState(null);
  const [sugg, setSugg] = useState([]);
  const suggT = useRef(null);

  const load = () => {
    setLoadErr("");
    api.get(`/scadenze/checklist?key=${encodeURIComponent(item.key)}&date=${item.date}`, { noCache: true })
      .then((r) => setData(r.data))
      .catch((e) => { setData({ rows: [], stats: {} }); setLoadErr(e.response?.data?.detail || e.message); });
  };
  useEffect(() => { setData(null); load(); }, [item]); // eslint-disable-line

  useEffect(() => {
    clearTimeout(suggT.current);
    if (newLink || newName.trim().length < 2) { setSugg([]); return; }
    suggT.current = setTimeout(() => {
      api.get(`/scadenze/redditi/suggest?q=${encodeURIComponent(newName.trim())}`)
        .then((r) => setSugg(r.data || [])).catch(() => setSugg([]));
    }, 250);
    return () => clearTimeout(suggT.current);
  }, [newName, newLink]);

  const toggle = async (row, field) => {
    const value = !row[field];
    const prev = data;
    setData((d) => ({
      ...d,
      rows: d.rows.map((r) => r.company_id === row.company_id
        ? { ...r, [field]: value, ...(field === "applicable" && !value ? { done: false, checked: false, sent: false } : {}) }
        : r),
    }));
    try {
      await api.put("/scadenze/checklist", { key: item.key, company_id: row.company_id, field, value });
    } catch (e) {
      setData(prev);
      alert("⚠️ SALVATAGGIO NON RIUSCITO: la spunta è stata annullata.\n" + (e.response?.data?.detail || e.message) + "\nControlla la connessione e riprova.");
    }
  };

  const addRow = async () => {
    if (!newName.trim()) return;
    try {
      const link = newLink || {};
      await api.post("/scadenze/checklist/add", {
        key: item.key, name: newName.trim(),
        company_id: link.kind === "company" ? link.id : null,
        person_id: link.kind === "person" ? link.id : null,
      });
      setNewName(""); setNewLink(null); setSugg([]);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const removeExtra = async (row) => {
    if (!window.confirm(`Togliere "${row.name}" da questa checklist?`)) return;
    try {
      await api.delete(`/scadenze/checklist/extra?key=${encodeURIComponent(item.key)}&company_id=${encodeURIComponent(row.company_id)}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const stats = useMemo(() => {
    if (!data) return null;
    const appl = data.rows.filter((r) => r.applicable);
    const done = appl.filter((r) => r.done).length;
    const checked = appl.filter((r) => r.checked).length;
    const sent = appl.filter((r) => r.sent).length;
    const n = appl.length;
    const wd = data.stats?.working_days_left || 0;
    const remaining = n - done;
    return {
      n, done, checked, sent,
      pctDone: n ? Math.round((done * 100) / n) : 0,
      pctChecked: n ? Math.round((checked * 100) / n) : 0,
      pctSent: n ? Math.round((sent * 100) / n) : 0,
      wd, remaining,
      perDay: wd > 0 && remaining > 0 ? Math.ceil(remaining / wd) : 0,
      expired: (data.stats?.working_days_left || 0) === 0 && item.date < new Date().toISOString().slice(0, 10),
    };
  }, [data, item.date]);

  const shown = (data?.rows || []).filter((r) => r.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="fiscal-checklist-modal">
      <div className="modal" style={{ maxWidth: 820, width: "94vw" }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="fiscal-checklist-close">✕</button>
        <h3 style={{ marginRight: 30 }}>📋 {item.label}</h3>
        <p className="muted" style={{ marginTop: -6 }}>Scadenza: <b>{fmtIt(item.date)}</b></p>

        {!data && <p className="muted">Caricamento clienti…</p>}
        {loadErr && (
          <p style={{ color: "#fb7185", fontSize: 13 }} data-testid="fc-load-error">
            ⚠️ Impossibile caricare la checklist: {loadErr}{" "}
            <button className="btn sm" onClick={load}>↻ Riprova</button>
          </p>
        )}
        {data && stats && (
          <>
            <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
              <div style={{ minWidth: 160, flex: 1 }}>
                <div style={{ fontSize: 12.5, marginBottom: 3 }}>✅ Fatto: <b data-testid="fc-done">{stats.done}/{stats.n}</b> ({stats.pctDone}%)</div>
                <div className="fc-bar"><div className="fc-bar-fill" style={{ width: `${stats.pctDone}%`, background: "#39ff14" }} /></div>
              </div>
              <div style={{ minWidth: 160, flex: 1 }}>
                <div style={{ fontSize: 12.5, marginBottom: 3 }}>🔎 Controllato: <b data-testid="fc-checked">{stats.checked}/{stats.n}</b> ({stats.pctChecked}%)</div>
                <div className="fc-bar"><div className="fc-bar-fill" style={{ width: `${stats.pctChecked}%`, background: "#facc15" }} /></div>
              </div>
              <div style={{ minWidth: 160, flex: 1 }}>
                <div style={{ fontSize: 12.5, marginBottom: 3 }}>📤 Inviato: <b data-testid="fc-sent">{stats.sent}/{stats.n}</b> ({stats.pctSent}%)</div>
                <div className="fc-bar"><div className="fc-bar-fill" style={{ width: `${stats.pctSent}%`, background: "#38bdf8" }} /></div>
              </div>
              <div style={{ fontSize: 12.5 }} data-testid="fc-rate">
                {stats.expired
                  ? <span style={{ color: "#fb7185" }}>⏰ Scadenza passata</span>
                  : <>📅 <b>{stats.wd}</b> giorni lavorativi rimasti<br />
                    {stats.remaining > 0
                      ? <>⚡ da fare <b>~{stats.perDay} al giorno</b> ({stats.remaining} rimanenti)</>
                      : <span style={{ color: "#39ff14" }}>🎉 tutte fatte!</span>}</>}
              </div>
            </div>

            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Cerca cliente…" style={{ width: "100%", marginBottom: 8 }} data-testid="fc-search" />

            <div style={{ maxHeight: "46vh", overflowY: "auto" }}>
              <table>
                <thead><tr>
                  <th>Cliente</th>
                  <th style={{ width: 100, textAlign: "center" }}>Interessato</th>
                  <th style={{ width: 80, textAlign: "center" }}>Fatta</th>
                  <th style={{ width: 100, textAlign: "center" }}>Controllata</th>
                  <th style={{ width: 80, textAlign: "center" }}>Inviata</th>
                  <th style={{ width: 36 }} />
                </tr></thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.company_id} style={{ opacity: r.applicable ? 1 : 0.45 }} data-testid={`fc-row-${r.company_id}`}>
                      <td style={{ textDecoration: r.applicable ? "none" : "line-through" }}>{r.name}{r.extra && <span className="muted" title="Aggiunto a mano a questa checklist" style={{ fontSize: 11 }}> ✚</span>}</td>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" checked={r.applicable} onChange={() => toggle(r, "applicable")}
                          title="Questo cliente ha questa scadenza?" data-testid={`fc-appl-${r.company_id}`} />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" checked={r.done} disabled={!r.applicable} onChange={() => toggle(r, "done")}
                          data-testid={`fc-done-${r.company_id}`} />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" checked={r.checked} disabled={!r.applicable} onChange={() => toggle(r, "checked")}
                          data-testid={`fc-checked-${r.company_id}`} />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" checked={r.sent} disabled={!r.applicable} onChange={() => toggle(r, "sent")}
                          data-testid={`fc-sent-${r.company_id}`} />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {r.extra && <button className="btn sm" style={{ padding: "0 6px" }} title="Togli dalla checklist" onClick={() => removeExtra(r)} data-testid={`fc-del-${r.company_id}`}>🗑</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <div style={{ flex: 1, position: "relative" }}>
                {sugg.length > 0 && (
                  <div className="rd-suggest" data-testid="fc-suggest-list">
                    {sugg.map((s) => (
                      <button key={`${s.kind}-${s.id}`} onClick={() => { setNewName(s.name); setNewLink(s); setSugg([]); }} data-testid={`fc-suggest-${s.id}`}>
                        {s.kind === "company" ? "🏢" : "👤"} {s.name}
                      </button>
                    ))}
                  </div>
                )}
                <input value={newName} onChange={(e) => { setNewName(e.target.value); setNewLink(null); }} onKeyDown={(e) => e.key === "Enter" && addRow()}
                  placeholder="+ Aggiungi un cliente a questa checklist… (cerca in anagrafica o testo libero)" style={{ width: "100%" }} data-testid="fc-new-name" />
                {newLink && <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>🔗 collegato a {newLink.kind === "company" ? "azienda" : "persona"} in anagrafica</div>}
              </div>
              <button className="btn sm primary" onClick={addRow} disabled={!newName.trim()} data-testid="fc-add-btn">Aggiungi</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
